import { useState } from 'react';

import { isPast, isValid } from 'date-fns';
import { useIntl } from 'react-intl';

import { SupportedLanguages } from '@rbi-ctg/frontend';
import { IDelivery } from '@rbi-ctg/menu';
import { useLocale } from 'state/intl';
import { DeliveryStatus } from 'state/order/types';
import { formatDistanceToNow } from 'utils/language/format-date';

import useInterval from '../use-interval';

const calcEtaRange = (
  text: Record<'delivered' | 'calculatingEta', string>,
  delivery: IDelivery,
  language: SupportedLanguages
) => {
  if (delivery.status === DeliveryStatus.ORDER_DROPPED_OFF) {
    return text.delivered;
  }

  const { arrivalTime, beforeTime } = delivery?.dropoff || {};

  // Bringg uses arrivalTime as ETA and beforeTime as a pessimistic ETA
  // We will use the latest of the 2 ETAs since the beforeTime is used on order start
  // but the arrivalTime is updated later on if a driver is unassigned, etc
  // Once the drive is on their way Bringg updates arrivalTime to be accurate based on driving time
  // so we will use that
  const arrivalDateTime = new Date(arrivalTime);
  const beforeDateTime = new Date(beforeTime);

  const pessimisticEta = arrivalDateTime > beforeDateTime ? arrivalDateTime : beforeDateTime;
  const deliveryDateTime =
    delivery.status === DeliveryStatus.ORDER_PICKED_UP ? arrivalDateTime : pessimisticEta;

  if (!isValid(deliveryDateTime)) {
    return text.calculatingEta;
  }

  if (isPast(deliveryDateTime)) {
    return text.calculatingEta;
  }

  return formatDistanceToNow(deliveryDateTime, language);
};

export const useEtaCounter = (delivery: IDelivery) => {
  const { language } = useLocale();
  const { formatMessage } = useIntl();
  const text = {
    delivered: formatMessage({ id: 'delivered' }),
    calculatingEta: formatMessage({ id: 'calculatingEta' }),
  };

  const [etaString, setEtaString] = useState<string>(calcEtaRange(text, delivery, language));

  useInterval(() => {
    setEtaString(calcEtaRange(text, delivery, language));
  }, 60000); // one minute in milliseconds

  return etaString;
};
